import type { Set } from 'immutable';

import type { BeneficiarySource } from '@/domain/payments/schemas';

import { asyncAction, syncAction } from '@/lib/action';

type SagaCallbacks = {
  resolve?: (value?: unknown) => void;
  reject?: (reason?: unknown) => void;
};

export const paymentsGridGetHeadersAction = asyncAction('PAYMENTS/GET_GRID_HEADERS');
export const paymentsGridGetRowsAction = asyncAction('PAYMENTS/GET_GRID_ROWS');
export const paymentsGridGetTotalByStateAction = syncAction('PAYMENTS/GET_GRID_TOTAL_BY_STATE');
export const paymentsGridClearTotalByStateAction = syncAction('PAYMENTS/CLEAR_GRID_TOTAL_BY_STATE');
export const paymentsGridFilterAppliedAction = syncAction('PAYMENTS/GRID_FILTER_APPLIED');
export const paymentsGridSortingAppliedAction = syncAction('PAYMENTS/GRID_SORTING_APPLIED');

export const paymentsGridPaymentsByPageIndexAction = syncAction('PAYMENTS/GRID_SET_PAYMENTS_BY_PAGE_INDEX');
export const paymentsGridSearchAfterByPageIndexAction = syncAction('PAYMENTS/GRID_SET_SEARCH_AFTER_BY_PAGE_INDEX');
export const paymentsGridDefaultColumnsAppliedAction = syncAction('PAYMENTS/GRID_DEFAULT_COLUMNS_APPLIED');
export const paymentsGridColumnsAppliedAction = syncAction('PAYMENTS/GRID_COLUMNS_APPLIED');
export const paymentsGridColumnsScopeChangedAction = syncAction<string | null>('PAYMENTS/GRID_COLUMNS_SCOPE_CHANGED');
export const paymentsGridClearConfigurationChangesAction = syncAction('PAYMENTS/GRID_CLEAR_CONFIGURATION_CHANGES');

export const paymentsGridRefreshServerSideAction = syncAction('PAYMENTS/GRID_REFRESH_SERVER_SIDE');
export const paymentsGridApplyTransactionAction = syncAction('PAYMENTS/GRID_APPLY_TRANSACTION');

export const paymentGetAction = asyncAction('PAYMENTS/GET_PAYMENT');
// Re-reads the payment for its `meta` alone — the option lists travel with it.
export const paymentRefreshMetaAction = asyncAction<{ id: string }>('PAYMENTS/REFRESH_PAYMENT_META');
// Raised by the request, lowered by this — one signal for every way that re-read can
// end, so no ending can leave a surface waiting on it.
export const paymentRefreshMetaSettledAction = syncAction('PAYMENTS/REFRESH_PAYMENT_META_SETTLED');
// An open bag: the payment id plus whichever info fields the caller is changing.
// Declared because the default payload type is `void`.
export type PaymentInfoUpdatePayload = {
  id: string;
  // Beneficiary-create envelope, spelled out so the saga's create call typechecks
  // against the endpoint body instead of widening to `unknown`.
  isNewBeneficiary?: boolean;
  details?: Record<string, string | null | undefined>;
  vendorId?: string;
  isDefault?: boolean;
  source?: BeneficiarySource;
  resolve?: (value: void | PromiseLike<void>) => void;
  reject?: (reason?: unknown) => void;
  [field: string]: unknown;
};
export const paymentUpdateInfoAction = asyncAction<PaymentInfoUpdatePayload>('PAYMENTS/UPDATE_PAYMENT_INFO');
export const paymentUpdateInvoicesAction = asyncAction('PAYMENTS/UPDATE_PAYMENT_INVOICES');
export const paymentUpdateInvoicesRollbackAction = asyncAction('PAYMENTS/UPDATE_PAYMENT_INVOICES_ROLLBACK');
export const clearPaymentAction = syncAction('PAYMENTS/CLEAR_PAYMENT');
export const startPaymentApprovalFlowAction = asyncAction('PAYMENTS/START_APPROVAL_FLOW');
export const resetPaymentProcessAction = asyncAction('PAYMENTS/RESET_APPROVAL_FLOW');
export const approvePaymentAction = asyncAction('PAYMENTS/APPROVE_PAYMENT');
export const rejectPaymentAction = asyncAction('PAYMENTS/REJECT_PAYMENT');
// `resolve` settles once the paid info is stored, so the page can act on a successful payment.
export const payPaymentAction = asyncAction<{ id: string } & SagaCallbacks>('PAYMENTS/PAY_PAYMENT');
export const voidPaymentAction = asyncAction('PAYMENTS/VOID_PAYMENT');
export const recreatePaymentAction = asyncAction('PAYMENTS/RECREATE_PAYMENT');
export const updatePaymentTagsAction = asyncAction('PAYMENTS/UPDATE_PAYMENT_TAGS');
export const unmergePaymentAction = asyncAction('PAYMENTS/UNMERGE_PAYMENT');
export const deletePaymentAction = asyncAction<{ id: string } & SagaCallbacks>('PAYMENTS/DELETE_PAYMENT');

export const paymentsBulkMergeAction = asyncAction('PAYMENTS/BULK_MERGE_PAYMENTS');
export const paymentsBulkSendToApprovalAction = asyncAction('PAYMENTS/BULK_SEND_TO_APPROVAL');
export const paymentsBulkSaveForLaterAction = asyncAction('PAYMENTS/BULK_SAVE_FOR_LATER');
export const paymentsBulkArchiveAction = asyncAction('PAYMENTS/BULK_ARCHIVE');
export const paymentsBulkApproveAction = asyncAction('PAYMENTS/BULK_APPROVE');
export const paymentsBulkResetApprovalFlowAction = asyncAction('PAYMENTS/BULK_RESET_APPROVAL_FLOW');
export const paymentsBulkPayAction = asyncAction('PAYMENTS/BULK_PAY');
export const paymentsBulkDeleteAction = asyncAction<{ paymentsIds: Set<string> } & SagaCallbacks>(
  'PAYMENTS/BULK_DELETE',
);

export const nextPaymentAction = asyncAction('PAYMENTS/NEXT');
export const setPaymentNavigationAction = syncAction<{
  search: { query: unknown; params: Readonly<Record<string, string | undefined>> };
  isSetRequest?: boolean;
}>('PAYMENTS/SET_NAVIGATION');
export const updatePaymentNavigationAction = syncAction('PAYMENTS/UPDATE_NAVIGATION');
export const clearPaymentNavigationAction = syncAction('PAYMENTS/CLEAR_NAVIGATION');
export const saveCachedPaymentAction = syncAction('PAYMENT/SAVE_CACHED_PAYMENTS');
